import React, { useEffect, useMemo, useState } from "react";
import { apiGetBroker, apiPostBroker, getApiBase } from "../api";
import { useAuth } from "../components/AuthContext";

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString();
  } catch {
    return String(dt);
  }
}
function section(title, children) {
  return (
    <div style={{ marginTop: 18, padding: 16, border: "1px solid #e5e5e5", borderRadius: 12, background: "white" }}>
      <div style={{ fontWeight: 900, marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}
function badgeStyle(txt) {
  const base = { display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 12, border: "1px solid #ddd" };
  const t = String(txt || "").toUpperCase();
  if (t === "PENDING") return { ...base, background: "#f3f3f3", borderColor: "#d9d9d9" };
  if (t === "APPROVED") return { ...base, background: "#eaffea", borderColor: "#9be79b" };
  if (t === "REJECTED") return { ...base, background: "#ffecec", borderColor: "#ffb3b3" };
  return base;
}

export default function BrokerPendingDocuments() {
  const apiBase = useMemo(() => getApiBase(), []);
  const { adminEmail } = useAuth();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [actionMsg, setActionMsg] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [notesByDocId, setNotesByDocId] = useState({});
  const [workingDocId, setWorkingDocId] = useState(null);

  async function refresh() {
    setErr("");
    setActionMsg("");
    setActionErr("");
    setLoading(true);
    try {
      const data = await apiGetBroker("/broker/documents/pending", { adminEmail });
      setDocs(data.documents || []);
    } catch (e) {
      setErr(e.message || String(e));
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }

  async function reviewDoc(loadId, docId, status) {
    setActionMsg("");
    setActionErr("");
    setWorkingDocId(docId);

    try {
      const notes = notesByDocId[docId] || "";
      await apiPostBroker(
        `/broker/loads/${loadId}/documents/${docId}/review`,
        { status, notes: notes ? notes : undefined },
        { adminEmail }
      );

      setActionMsg(`Document ${status} (Safer snapshot captured).`);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      setActionErr(e.message || String(e));
    } finally {
      setWorkingDocId(null);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminEmail]);

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={refresh} style={{ padding: "10px 14px", fontWeight: 900 }} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>API: {apiBase}</div>
      </div>

      {err ? (
        <div style={{ marginTop: 14, padding: 12, background: "#ffecec", border: "1px solid #ffb3b3", borderRadius: 10 }}>
          {err}
        </div>
      ) : null}

      {actionMsg ? (
        <div style={{ marginTop: 14, padding: 12, background: "#eaffea", border: "1px solid #9be79b", borderRadius: 10 }}>
          {actionMsg}
        </div>
      ) : null}

      {actionErr ? (
        <div style={{ marginTop: 14, padding: 12, background: "#ffecec", border: "1px solid #ffb3b3", borderRadius: 10 }}>
          {actionErr}
        </div>
      ) : null}

      {section(
        `Pending Documents (${docs.length})`,
        loading ? (
          <div>Loading…</div>
        ) : docs.length ? (
          <div style={{ display: "grid", gap: 10 }}>
            {docs.map((d) => {
              const load = d.load;
              const shipper = load?.shipper?.name || "—";
              const carrier = load?.carrier?.name || "—";
              const lane = `${load?.originAddress || "—"} → ${load?.destAddress || "—"}`;
              const ref = load?.referenceNumber || load?.id || "—";

              return (
                <div key={d.id} style={{ border: "1px solid #eee", borderRadius: 12, padding: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 900 }}>
                      {d.type} • {d.fileName}
                    </div>
                    <span style={badgeStyle(d.reviewStatus)}>{d.reviewStatus}</span>
                    <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>
                      Uploaded {fmt(d.createdAt)}
                    </div>
                  </div>

                  <div style={{ marginTop: 8, fontSize: 13, color: "#444" }}>
                    <b>Load:</b> {ref} &nbsp;&nbsp; <b>Lane:</b> {lane}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, color: "#444" }}>
                    <b>Shipper:</b> {shipper} &nbsp;&nbsp; <b>Carrier:</b> {carrier}
                  </div>

                  <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
                    storageKey: <code>{d.storageKey}</code>
                  </div>

                  <div style={{ display: "flex", gap: 10, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <input
                      placeholder="Optional review notes (shown to carrier if rejected)"
                      value={notesByDocId[d.id] || ""}
                      onChange={(e) => setNotesByDocId((p) => ({ ...p, [d.id]: e.target.value }))}
                      style={{ flex: "1 1 520px", padding: 10 }}
                      disabled={workingDocId === d.id}
                    />

                    <button
                      onClick={() => reviewDoc(load?.id, d.id, "APPROVED")}
                      disabled={workingDocId === d.id}
                      style={{ padding: "10px 14px", fontWeight: 900 }}
                    >
                      {workingDocId === d.id ? "Working..." : "Approve"}
                    </button>

                    <button
                      onClick={() => reviewDoc(load?.id, d.id, "REJECTED")}
                      disabled={workingDocId === d.id}
                      style={{ padding: "10px 14px", fontWeight: 900 }}
                    >
                      {workingDocId === d.id ? "Working..." : "Reject"}
                    </button>
                  </div>

                  <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
                    Approve/Reject triggers an automated SaferWebAPI snapshot.
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ color: "#666" }}>No pending documents.</div>
        )
      )}
    </div>
  );
}
